import { useAnimations, useGLTF } from '@react-three/drei'
import { observer } from 'mobx-react-lite'
import { animate } from 'motion'
import { RefObject, useEffect, useRef, useState } from 'react'
import {
    AdditiveAnimationBlendMode,
    AnimationAction,
    AnimationClip,
    AnimationMixer,
    AnimationUtils,
    Color,
    Group,
    LoopOnce,
    Mesh,
    MeshBasicMaterial,
    NoColorSpace,
    SRGBColorSpace,
    type Object3D,
} from 'three'
import { useRunState } from '@/src/game/provider/useRunState'
import { gearNames } from './characterGear'
import {
    characterEndCardDuration,
    characterEndCardRotationY,
    characterPreRunDefaultRotationY,
    characterRotationDragSpeed,
    characterRotationResetDuration,
    effectPreviewDistance,
    effectPreviewDuration,
    effectPreviewHeight,
    chameleonFresnelPower,
    chameleonFresnelRimStart,
    characterOutlineCollectAdditive,
    characterOutlineCollectAdditiveMobile,
    characterOutlineCollectCoinColor,
    characterOutlineCollectGlow,
    characterOutlineCollectGlowMobile,
    characterOutlineCollectHeartColor,
    characterOutlineCollectPulsePeriod,
    characterOutlineCollectPulsePeriodMobile,
    characterOutlineCollectShieldColor,
    characterOutlineCollectSoftness,
    characterOutlineCollectSoftnessMobile,
    characterOutlineCollectStrength,
    characterOutlineCollectStrengthMobile,
    characterOutlineCollectThickness,
    characterOutlineCollectThicknessMobile,
    characterOutlineAdditive,
    characterOutlineAdditiveMobile,
    characterOutlineColor,
    characterOutlineGlow,
    characterOutlineGlowMobile,
    characterOutlinePulsePeriod,
    characterOutlinePulsePeriodMobile,
    characterOutlineSoftness,
    characterOutlineSoftnessMobile,
    characterOutlineStrength,
    characterOutlineStrengthMobile,
    characterOutlineThickness,
    characterOutlineThicknessMobile,
    endCardDepthOffset,
    endCardVerticalOffset,
} from '@/src/game/config/constants'
import { store } from '@/src/stores/store'
import { useFrame, useThree } from '@react-three/fiber'
import {
    CharacterOutlineEffect,
    createCharacterOutlineController,
    type OutlineHandle,
} from './outline'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import { gltfAsset, textureAssetCompressed } from '@/src/game/assetStore'
import { useFootstepTimeline } from '../../effects/useFootstepTimeline'
import { usePreviewTimeline } from '../../effects/usePreviewTimeline'
import { EffectRunFootsteps } from '../../effects/EffectRunFootsteps'
import { EffectRunDust } from '../../effects/EffectRunDust'
import { EffectRunLeaves } from '../../effects/EffectRunLeaves'
import { EffectRunGlow } from '../../effects/EffectRunGlow'
import { EffectJump } from '../../effects/EffectJump'
import { ShieldBreakEffect } from './ShieldBreakEffect'
import { CharacterShadow } from './CharacterShadow'
import { LEVELS } from '../../config/levels'
import { applyOutlineState, applySkinColorState, applySkinFlashColor, CharacterSkinMaterial, createCharacterBasicMaterial, getMeshBasicMaterials, isTexturableColorMaterial, resolveCharacterBasicMaterialTargets, resolveCharacterSkinMaterial, resolveCharacterSkinMaterials, setCharacterFresnelParams, triggerSkinFlash, withHalfSaturation } from './characterMaterialHelpers'
import { useCompressedTexture } from '../../ktx2Textures'
import { LevelCompleteCard } from '../card/LevelCompleteCard'
import { useAtlasMaterial } from '../../materials/atlasMaterial'

// Feature Flags
const ENABLE_ADDITIVE_ANIMATIONS = false

// Konstanten
const chameleonTextureUrlById = {
    '01': textureAssetCompressed.Chameleon_Color_01,
    '02': textureAssetCompressed.Chameleon_Color_04,
    '03': textureAssetCompressed.Chameleon_Color_03,
    '04': textureAssetCompressed.Chameleon_Color_02,
} as const

type ChameleonTextureId = keyof typeof chameleonTextureUrlById

const upgradeItemNames = [
    'Item_Boots_low',
    'Item_Gloves_Low',
    'Item_Helmet_low',
    'Item_Vest_low',
] as const

const atlasCharacterItemNames = [
    ...upgradeItemNames,
    ...gearNames,
] as const

const FLASH_DURATION = 0.2
const EQUIP_CLIP_NAME = 'Add_Equipt.03'
const REFERENCE_CLIP_NAME = 'ReferencePose'
const characterPositionOffset: [number, number, number] = [0, 0, 0]
const HIGH_FIVE_STAR_EFFECT_START_EVENT = 'highfive-star-effect-start'
const HIGH_FIVE_STAR_EFFECT_END_EVENT = 'highfive-star-effect-end'
const fullRotation = Math.PI * 2


// Hilfsfunktionen
function getCharacterPhaseRotationTarget(appFlowPhase: string, runPhase: string): number {
    if (runPhase === 'endCard') return characterEndCardRotationY
    if (
        appFlowPhase === 'splash' ||
        appFlowPhase === 'start' ||
        appFlowPhase === 'intro' ||
        appFlowPhase === 'configurator'
    ) {
        return characterPreRunDefaultRotationY
    }
    return 0
}

function getCharacterFresnelRimEndTarget(appFlowPhase: string): number {
    return appFlowPhase === 'run' ? 0 : 1
}

function getNearestFullRotationTarget(angle: number): number {
    return Math.round(angle / fullRotation) * fullRotation
}

function applyMaterialToObject(root: Object3D | null, material: MeshBasicMaterial) {
    if (!root) return

    root.traverse((child) => {
        if (!(child instanceof Mesh)) return
        child.material = material
    })
}
// Typen
type CharacterProps = {
    laneTurnAnimationRef: RefObject<number>
    registerActionsRef: RefObject<(
        actions: Record<string, AnimationAction | null>,
        mixer: AnimationMixer,
        clips: AnimationClip[],
    ) => void>
    runAnimationTimeScaleRef: RefObject<number>
}


// Komponente
export const Character = observer(function Character({
    laneTurnAnimationRef,
    registerActionsRef,
    runAnimationTimeScaleRef,
}: CharacterProps) {
    const runState = useRunState()
    const snapshot = runState.snapshot
    const isStele = useSteleBreakpoint()

    const outlineDefaultThickness = isStele
        ? characterOutlineThickness
        : characterOutlineThicknessMobile
    const outlineDefaultAdditive = isStele
        ? characterOutlineAdditive
        : characterOutlineAdditiveMobile
    const outlineDefaultGlow = isStele
        ? characterOutlineGlow
        : characterOutlineGlowMobile
    const outlineDefaultPulsePeriod = isStele
        ? characterOutlinePulsePeriod
        : characterOutlinePulsePeriodMobile
    const outlineDefaultSoftness = isStele
        ? characterOutlineSoftness
        : characterOutlineSoftnessMobile
    const outlineDefaultStrength = isStele
        ? characterOutlineStrength
        : characterOutlineStrengthMobile
    const outlineCollectThickness = isStele
        ? characterOutlineCollectThickness
        : characterOutlineCollectThicknessMobile
    const outlineCollectAdditive = isStele
        ? characterOutlineCollectAdditive
        : characterOutlineCollectAdditiveMobile
    const outlineCollectGlow = isStele
        ? characterOutlineCollectGlow
        : characterOutlineCollectGlowMobile
    const outlineCollectPulsePeriod = isStele
        ? characterOutlineCollectPulsePeriod
        : characterOutlineCollectPulsePeriodMobile
    const outlineCollectSoftness = isStele
        ? characterOutlineCollectSoftness
        : characterOutlineCollectSoftnessMobile
    const outlineCollectStrength = isStele
        ? characterOutlineCollectStrength
        : characterOutlineCollectStrengthMobile

    // Outline-Controller einmalig initialisieren (kein useEffect nÃƒÂ¶tig)
    const outlineControllerRef = useRef<ReturnType<typeof createCharacterOutlineController> | null>(null)
    if (!outlineControllerRef.current) {
        outlineControllerRef.current = createCharacterOutlineController(
            outlineDefaultThickness,
            characterOutlineColor,
            outlineDefaultStrength,
            outlineDefaultGlow,
            outlineDefaultPulsePeriod,
            outlineDefaultSoftness,
            outlineDefaultAdditive,
        )
    }
    const outlineController = outlineControllerRef.current
    const outlineHandleRef = useRef<OutlineHandle | null>(outlineController.handle)

    // Configurator-Rotation
    const configuratorIsDraggingRef = useRef(false)
    const configuratorRotationRef = useRef(0)
    const configuratorLastClientXRef = useRef(0)
    const configuratorResetAnimationRef = useRef<ReturnType<typeof animate> | null>(null)
    const previousAppFlowPhaseRef = useRef(store.appFlowPhase)

    // Phase-Rotation
    // const phaseRotationRef = useRef(getCharacterPhaseRotationTarget(store.appFlowPhase, runState.phase))
    // const phaseRotationAnimationRef = useRef<ReturnType<typeof animate> | null>(null)

    // Animations
    const playEquipAnimRef = useRef<(() => void) | null>(null)

    // Scene-Refs
    const configuratorRotationGroupRef = useRef<Group>(null)
    const laneTurnRotationGroupRef = useRef<Group>(null)
    const effectGroupRef = useRef<Group>(null)
    const previewEffectRotationGroupRef = useRef<Group>(null)
    const previewEffectAnchorRef = useRef<Group>(null)
    const phaseRotationAnimationRef = useRef<ReturnType<typeof animate> | null>(null)
    const phaseRotationRef = useRef(
        getCharacterPhaseRotationTarget(store.appFlowPhase, runState.phase),
    )
    const skinMeshRef = useRef<Mesh | null>(null)
    const skinMaterialRef = useRef<MeshBasicMaterial | null>(null)
    const skinMaterialsRef = useRef<MeshBasicMaterial[]>([])
    const tongueMaterialRef = useRef<MeshBasicMaterial | null>(null)
    const eyeMaterialRef = useRef<MeshBasicMaterial | null>(null)
    const shoeColorMaterialsRef = useRef<MeshBasicMaterial[]>([])
    const convertedBasicMaterialsRef = useRef<MeshBasicMaterial[]>([])

    // Effekte & Timings
    const planeHueRef = useRef(0)
    const _outlineColor = useRef(new Color())
    const previousLevelRef = useRef(-1)
    const outlineFlashRef = useRef<{ color: string; remaining: number } | null>(null)
    const skinFlashRef = useRef<{ color: Color; remaining: number } | null>(null)
    const previousHitReactionActiveRef = useRef(false)
    const [temporaryPlaneEffectActive, setTemporaryPlaneEffectActive] = useState(false)

    // Effect-Timelines
    const footstepTimelineRef = useFootstepTimeline(runAnimationTimeScaleRef)
    const previewTimelineRef = usePreviewTimeline()
    const previewEffectRotationAnimationRef = useRef<ReturnType<typeof animate> | null>(null)

    const { scene, animations, materials } = useGLTF(gltfAsset.Chameleon_01)
    const { actions, mixer, clips } = useAnimations(animations, scene)


    const allChameleonTextures: Record<ChameleonTextureId, Texture> = {
        '01': useCompressedTexture(chameleonTextureUrlById['01']),
        '02': useCompressedTexture(chameleonTextureUrlById['02']),
        '03': useCompressedTexture(chameleonTextureUrlById['03']),
        '04': useCompressedTexture(chameleonTextureUrlById['04']),
    }

    const selectedChameleonTextureId = (
        store.chameleonTexture in chameleonTextureUrlById
            ? store.chameleonTexture
            : '01'
    ) as ChameleonTextureId
    const chameleonTexture = allChameleonTextures[selectedChameleonTextureId]
    const tongueTexture = useCompressedTexture(textureAssetCompressed.Tongue)
    const eyeTexture = useCompressedTexture(textureAssetCompressed.Eyes_Color)
    const planeTexture = useCompressedTexture(textureAssetCompressed.Chameleon_Color_06)
    const shieldTexture = useCompressedTexture(textureAssetCompressed.Chameleon_Color_08)

    const { gl } = useThree()
    const atlasMaterial = useAtlasMaterial()

    const isPlaneActive = snapshot.planeTimer > 0
    const isPlaneVisualActive = isPlaneActive || temporaryPlaneEffectActive
    const isShieldActive = snapshot.shieldActive
    const isShieldVisualActive =
        isShieldActive &&
        !snapshot.finishLineReached
        &&
        runState.phase !== 'endCard'
    const isBatteryActive = snapshot.batteryCount > 0
    const selectedDecorationItemsKey = Array.from(store.selectedDecorationItems)
        .sort()
        .join('|')
    const selectedGearItemsKey = Array.from(store.selectedGearItems)
        .sort((a, b) => a - b)
        .join('|')

    // Kombinierte Rotation (Phase + Configurator-Drag) anwenden
    const applyCharacterRotation = () => {
        if (!configuratorRotationGroupRef.current) return
        configuratorRotationGroupRef.current.rotation.y =
            phaseRotationRef.current + configuratorRotationRef.current
    }


    // Animation-Clip Bereinigung
    useEffect(() => {
        for (const clip of clips) {
            const epsilon = 1e-4
            for (const track of clip.tracks) {
                if (!track.times || track.times.length < 2) continue
                const lastIndex = track.times.length - 1
                if (Math.abs(track.times[lastIndex] - clip.duration) >= epsilon) continue
                const valueSize = track.getValueSize()
                const lastOffset = lastIndex * valueSize
                let isDuplicate = true
                for (let i = 0; i < valueSize; i++) {
                    if (Math.abs(track.values[i] - track.values[lastOffset + i]) > epsilon) {
                        isDuplicate = false
                        break
                    }
                }
                if (!isDuplicate) continue
                track.times = track.times.slice(0, lastIndex)
                track.values = track.values.slice(0, lastOffset)
            }
            clip.resetDuration()
        }
    }, [clips])


    // Actions registrieren
    useEffect(() => {
        if (Object.keys(actions).length > 0) {
            registerActionsRef.current?.(actions, mixer, clips)
        }
    }, [actions, mixer, clips, registerActionsRef])


    // Texturen initialisieren
    useEffect(() => {
        Object.values(allChameleonTextures).forEach(texture => {
            texture.colorSpace = SRGBColorSpace
            texture.flipY = false
            texture.needsUpdate = true
            gl.initTexture(texture)
        })
    }, [gl, allChameleonTextures])

    useEffect(() => {
        tongueTexture.colorSpace = SRGBColorSpace
        tongueTexture.flipY = false
        tongueTexture.needsUpdate = true
        gl.initTexture(tongueTexture)
    }, [gl, tongueTexture])

    useEffect(() => {
        eyeTexture.colorSpace = SRGBColorSpace
        eyeTexture.flipY = false
        eyeTexture.needsUpdate = true
        gl.initTexture(eyeTexture)
    }, [gl, eyeTexture])


    // Scene Setup + Material-Konvertierung
    useEffect(() => {
        scene.position.set(0.0, -endCardVerticalOffset, endCardDepthOffset)
        scene.traverse((object: Object3D) => {
            if (!(object instanceof Mesh)) return
            object.castShadow = false
            object.receiveShadow = false
            object.frustumCulled = true
        })

        const importedBasicMaterialTargets = resolveCharacterBasicMaterialTargets(materials)
        if (importedBasicMaterialTargets.length > 0) {
            const replacementMap = new Map<CharacterSkinMaterial, MeshBasicMaterial>()
            importedBasicMaterialTargets.forEach((sourceMaterial) => {
                replacementMap.set(sourceMaterial, createCharacterBasicMaterial(sourceMaterial))
            })

            convertedBasicMaterialsRef.current = Array.from(replacementMap.values())
            const importedSkinMaterial = resolveCharacterSkinMaterial(materials)
            const importedSkinMaterials = resolveCharacterSkinMaterials(materials)

            skinMaterialRef.current = importedSkinMaterial
                ? (replacementMap.get(importedSkinMaterial) ?? null)
                : null
            skinMaterialsRef.current = importedSkinMaterials
                .map(m => replacementMap.get(m) ?? null)
                .filter((m): m is MeshBasicMaterial => m != null)

            const importedTongueMaterial =
                isTexturableColorMaterial(materials.Tongue) ? materials.Tongue :
                    isTexturableColorMaterial(materials.Chameleon_Tongue) ? materials.Chameleon_Tongue : null
            const importedEyeMaterial =
                isTexturableColorMaterial(materials.Eyes) ? materials.Eyes :
                    isTexturableColorMaterial(materials.Chameleon_Eyes) ? materials.Chameleon_Eyes : null

            tongueMaterialRef.current = importedTongueMaterial
                ? (replacementMap.get(importedTongueMaterial) ?? null)
                : null
            eyeMaterialRef.current = importedEyeMaterial
                ? (replacementMap.get(importedEyeMaterial) ?? null)
                : null

            scene.traverse((object: Object3D) => {
                if (!(object instanceof Mesh)) return
                if (Array.isArray(object.material)) {
                    object.material = object.material.map(
                        m => replacementMap.get(m as CharacterSkinMaterial) ?? m,
                    )
                } else {
                    const replacement = replacementMap.get(object.material as CharacterSkinMaterial)
                    if (replacement) object.material = replacement
                }
            })
        }

        const skinMeshObj = scene.getObjectByName('Chameleon_Body_low')
        skinMeshRef.current = skinMeshObj instanceof Mesh ? skinMeshObj : null

        const shoeColorMeshObj = scene.getObjectByName('Chameleon_Shoes_Color')
        if (shoeColorMeshObj instanceof Mesh) {
            shoeColorMaterialsRef.current = (Array.isArray(shoeColorMeshObj.material)
                ? shoeColorMeshObj.material
                : [shoeColorMeshObj.material]
            ).filter((material): material is MeshBasicMaterial => material instanceof MeshBasicMaterial)
        } else {
            shoeColorMaterialsRef.current = []
        }

        const hitbox = scene.getObjectByName('Chameleon_Hitbox')
        if (hitbox) hitbox.visible = false

        atlasCharacterItemNames.forEach((name) => {
            applyMaterialToObject(scene.getObjectByName(name), atlasMaterial)
        })

        return () => {
            convertedBasicMaterialsRef.current.forEach(m => m.dispose())
            convertedBasicMaterialsRef.current = []
            skinMeshRef.current = null
            skinMaterialRef.current = null
            skinMaterialsRef.current = []
            tongueMaterialRef.current = null
            eyeMaterialRef.current = null
        }
    }, [scene, materials, atlasMaterial])


    // Textur-Zuweisungen
    useEffect(() => {
        const activeSkinMaterials = getMeshBasicMaterials(skinMeshRef.current)
        if (activeSkinMaterials.length === 0) return
        skinMaterialsRef.current = activeSkinMaterials
        activeSkinMaterials.forEach((m) => {
            m.map = chameleonTexture
        })
    }, [chameleonTexture, materials])

    useEffect(() => {
        if (!tongueMaterialRef.current) return
        tongueMaterialRef.current.map = tongueTexture
    }, [tongueTexture, materials])

    useEffect(() => {
        if (!eyeMaterialRef.current) return
        eyeMaterialRef.current.map = eyeTexture
    }, [eyeTexture, materials])

    useEffect(() => {
        const shoeUvOffsetByTextureId: Record<string, number> = {
            '02': 0.625,
            '03': 0.125,
            '04': 0.25,
        }
        const offsetX = shoeUvOffsetByTextureId[store.chameleonTexture] ?? 0

        shoeColorMaterialsRef.current.forEach((material) => {
            if (!material.map) return
            if (material.map.userData.characterShoeUvClone !== true) {
                material.map = material.map.clone()
                material.map.userData.characterShoeUvClone = true
                material.map.colorSpace = SRGBColorSpace
                material.map.flipY = false
            }
            material.map.offset.x = offsetX
            material.map.needsUpdate = true
            material.needsUpdate = true
        })
    }, [store.chameleonTexture])

    useEffect(() => {
        const activeSkinMaterials = getMeshBasicMaterials(skinMeshRef.current)
        if (activeSkinMaterials.length === 0) return
        skinMaterialsRef.current = activeSkinMaterials
        activeSkinMaterials.forEach((m) => {
            m.map = isPlaneActive ? planeTexture : isShieldVisualActive ? shieldTexture : chameleonTexture
        })
    }, [isPlaneActive, isShieldVisualActive, isBatteryActive, chameleonTexture])


    // Fresnel-Parameter
    useEffect(() => {
        getMeshBasicMaterials(skinMeshRef.current).forEach((m) => {
            setCharacterFresnelParams(
                m,
                chameleonFresnelPower,
                chameleonFresnelRimStart,
                getCharacterFresnelRimEndTarget(store.appFlowPhase),
            )
        })
    }, [
        store.appFlowPhase,
        chameleonFresnelPower,
        chameleonFresnelRimStart,
    ])


    // Outline-State
    useEffect(() => {
        applyOutlineState({
            outline: outlineHandleRef.current,
            defaultThickness: outlineDefaultThickness,
            defaultColor: characterOutlineColor,
            defaultStrength: outlineDefaultStrength,
            defaultGlow: outlineDefaultGlow,
            defaultPulsePeriod: outlineDefaultPulsePeriod,
            defaultSoftness: outlineDefaultSoftness,
            defaultAdditive: outlineDefaultAdditive,
            collectThickness: outlineCollectThickness,
            collectShieldColor: characterOutlineCollectShieldColor,
            collectStrength: outlineCollectStrength,
            collectGlow: outlineCollectGlow,
            collectPulsePeriod: outlineCollectPulsePeriod,
            collectSoftness: outlineCollectSoftness,
            collectAdditive: outlineCollectAdditive,
            isPlaneActive: isPlaneVisualActive,
            isShieldActive: isShieldVisualActive,
        })
    }, [
        isPlaneVisualActive,
        isShieldVisualActive,
        outlineDefaultThickness,
        characterOutlineColor,
        outlineDefaultStrength,
        outlineDefaultGlow,
        outlineDefaultPulsePeriod,
        outlineDefaultSoftness,
        outlineDefaultAdditive,
        outlineCollectThickness,
        outlineCollectStrength,
        outlineCollectGlow,
        outlineCollectPulsePeriod,
        outlineCollectSoftness,
        outlineCollectAdditive,
        characterOutlineCollectShieldColor,
    ])


    // Collect-Events (Coin / Heart)
    useEffect(() => {
        function triggerCollectFlash(outlineColor: string) {
            skinMaterialsRef.current = getMeshBasicMaterials(skinMeshRef.current)
            outlineHandleRef.current?.setThickness(outlineCollectThickness)
            outlineHandleRef.current?.setStrength(outlineCollectStrength)
            outlineHandleRef.current?.setGlow(outlineCollectGlow)
            outlineHandleRef.current?.setPulsePeriod(outlineCollectPulsePeriod)
            outlineHandleRef.current?.setDownSampleRatio(outlineCollectSoftness)
            outlineHandleRef.current?.setAdditive(outlineCollectAdditive)
            outlineHandleRef.current?.setColor(withHalfSaturation(outlineColor))
            triggerSkinFlash(skinFlashRef, outlineColor, FLASH_DURATION)
            outlineFlashRef.current = { color: outlineColor, remaining: FLASH_DURATION }
        }

        const handleCoin = () => triggerCollectFlash(characterOutlineCollectCoinColor)
        const handleHeart = () => triggerCollectFlash(characterOutlineCollectHeartColor)

        window.addEventListener('coin-collect', handleCoin)
        window.addEventListener('heart-collect', handleHeart)
        return () => {
            window.removeEventListener('coin-collect', handleCoin)
            window.removeEventListener('heart-collect', handleHeart)
        }
    }, [
        characterOutlineCollectCoinColor,
        characterOutlineCollectHeartColor,
        outlineCollectThickness,
        outlineCollectStrength,
        outlineCollectGlow,
        outlineCollectPulsePeriod,
        outlineCollectSoftness,
        outlineCollectAdditive,
    ])


    // Item-Sichtbarkeit (Upgrade-Unlock)
    // Gear visibility is driven reactively by the persistent `unlockedGearLevel`
    // so it survives run restarts (which reset score/points but keep the gear).
    useEffect(() => {
        upgradeItemNames.forEach((name, index) => {
            const obj = scene.getObjectByName(name)
            if (obj) {
                obj.visible =
                    index < store.unlockedGearLevel &&
                    store.selectedGearItems.has(index)
            }
        })

        gearNames.forEach((name) => {
            const obj = scene.getObjectByName(name)
            if (obj) {
                obj.visible = store.selectedDecorationItems.has(name)
            }
        })

        atlasCharacterItemNames.forEach((name) => {
            applyMaterialToObject(scene.getObjectByName(name), atlasMaterial)
        })

        outlineHandleRef.current?.syncVisibility()
    }, [scene, store.unlockedGearLevel, selectedGearItemsKey, selectedDecorationItemsKey, atlasMaterial])


    // Level-Up
    const unlockedGearCount = LEVELS.filter(
        ({ end }) => Math.floor(runState.points) >= end,
    ).length

    useEffect(() => {
        if (store.appFlowPhase !== 'run') {
            previousLevelRef.current = -1
            return
        }
        if (previousLevelRef.current === -1) {
            previousLevelRef.current = unlockedGearCount
            return
        }
        if (unlockedGearCount <= previousLevelRef.current) return
        previousLevelRef.current = unlockedGearCount

        const previousGearLevel = store.unlockedGearLevel
        // Only fire the unlock event + equip animation when the run actually
        // pushes the gear past what the character already owns.

        store.raiseGearLevel(unlockedGearCount)

        if (store.unlockedGearLevel > previousGearLevel) {
            window.dispatchEvent(new CustomEvent('upgrade-unlock', {
                detail: { unlockedCount: store.unlockedGearLevel },
            }))
        }
    }, [runState.points, store.appFlowPhase, store.unlockedGearLevel, unlockedGearCount])


    // Configurator-Rotation Reset
    useEffect(() => {
        const previousAppFlowPhase = previousAppFlowPhaseRef.current
        previousAppFlowPhaseRef.current = store.appFlowPhase

        if (previousAppFlowPhase !== 'configurator' || store.appFlowPhase === 'configurator') return

        configuratorIsDraggingRef.current = false
        configuratorResetAnimationRef.current?.stop()
        const targetRotation = getNearestFullRotationTarget(configuratorRotationRef.current)
        configuratorResetAnimationRef.current = animate(configuratorRotationRef.current, targetRotation, {
            duration: characterRotationResetDuration,
            ease: 'easeInOut',
            onUpdate: (latest) => {
                configuratorRotationRef.current = latest
                applyCharacterRotation()
            },
            onComplete: () => {
                configuratorResetAnimationRef.current = null
                configuratorRotationRef.current = 0
                applyCharacterRotation()
            },
        })
    }, [store.appFlowPhase])


    // High-Five Temporary Plane Effect
    useEffect(() => {
        const handleStart = () => setTemporaryPlaneEffectActive(true)
        const handleEnd = () => setTemporaryPlaneEffectActive(false)
        window.addEventListener(HIGH_FIVE_STAR_EFFECT_START_EVENT, handleStart)
        window.addEventListener(HIGH_FIVE_STAR_EFFECT_END_EVENT, handleEnd)
        return () => {
            window.removeEventListener(HIGH_FIVE_STAR_EFFECT_START_EVENT, handleStart)
            window.removeEventListener(HIGH_FIVE_STAR_EFFECT_END_EVENT, handleEnd)
            setTemporaryPlaneEffectActive(false)
        }
    }, [])


    // Phase-Rotation Animation
    useEffect(() => {
        const targetRotation = getCharacterPhaseRotationTarget(store.appFlowPhase, runState.phase)

        phaseRotationAnimationRef.current?.stop()
        phaseRotationAnimationRef.current = null

        const duration = runState.phase === 'endCard'
            ? characterEndCardDuration
            : 0

        if (duration <= 0) {
            phaseRotationRef.current = targetRotation
            applyCharacterRotation()
            return
        }

        phaseRotationAnimationRef.current = animate(phaseRotationRef.current, targetRotation, {
            duration,
            ease: 'easeInOut',
            onUpdate: (latest) => {
                phaseRotationRef.current = latest
                applyCharacterRotation()
            },
            onComplete: () => {
                phaseRotationAnimationRef.current = null
                phaseRotationRef.current = targetRotation
                applyCharacterRotation()
            },
        })
    }, [runState.phase, store.appFlowPhase])


    // Effect-Preview Rotation
    useEffect(() => {
        const showPreview =
            store.appFlowPhase === 'configurator' &&
            store.configuratorActiveTab === 'effect'

        previewEffectRotationAnimationRef.current?.stop()
        previewEffectRotationAnimationRef.current = null

        if (!showPreview || !previewEffectRotationGroupRef.current) {
            if (previewEffectRotationGroupRef.current) {
                previewEffectRotationGroupRef.current.rotation.y = 0
            }
            return
        }

        previewEffectRotationGroupRef.current.rotation.y = 0
        previewEffectRotationAnimationRef.current = animate(0, fullRotation, {
            duration: effectPreviewDuration,
            ease: 'linear',
            repeat: Infinity,
            onUpdate: (latest) => {
                if (!previewEffectRotationGroupRef.current) return
                previewEffectRotationGroupRef.current.rotation.y = latest
            },
        })

        return () => {
            previewEffectRotationAnimationRef.current?.stop()
            previewEffectRotationAnimationRef.current = null
            if (previewEffectRotationGroupRef.current) {
                previewEffectRotationGroupRef.current.rotation.y = 0
            }
        }
    }, [store.appFlowPhase, store.configuratorActiveTab])


    // Equip-Animation (additiv)
    useEffect(() => {
        if (!ENABLE_ADDITIVE_ANIMATIONS) {
            playEquipAnimRef.current = null
            return
        }

        const rawClip = clips.find(c => c.name === EQUIP_CLIP_NAME)
        if (!rawClip) return

        if (!rawClip.userData.equipAdditiveClipPrepared) {
            const referenceClip =
                clips.find(c => c.name === REFERENCE_CLIP_NAME) ??
                clips.find(c => c.name.toLowerCase().includes('idle')) ??
                clips[0]
            const sourceClip = rawClip.clone()
            const additiveClip = referenceClip
                ? AnimationUtils.makeClipAdditive(sourceClip, 0, referenceClip, 30)
                : AnimationUtils.makeClipAdditive(sourceClip)
            additiveClip.name = rawClip.name
            rawClip.userData.equipAdditiveClip = additiveClip
            rawClip.userData.equipAdditiveClipPrepared = true
        }

        const preparedClip = rawClip.userData.equipAdditiveClip as AnimationClip
        const equipAction = mixer.clipAction(preparedClip, undefined, AdditiveAnimationBlendMode)

        playEquipAnimRef.current = () => {
            equipAction.setLoop(LoopOnce, 1)
            equipAction.clampWhenFinished = false
            equipAction.setEffectiveWeight(1)
            equipAction.setEffectiveTimeScale(1)
            equipAction.reset()
            equipAction.play()
        }
    }, [clips, mixer])

    useEffect(() => {
        if (!ENABLE_ADDITIVE_ANIMATIONS) return
        playEquipAnimRef.current?.()
    }, [store.chameleonTexture, store.chameleonColor])

    useEffect(() => {
        if (!ENABLE_ADDITIVE_ANIMATIONS) return
        const handleCollect = () => playEquipAnimRef.current?.()
        window.addEventListener('powerup-collect', handleCollect)
        return () => window.removeEventListener('powerup-collect', handleCollect)
    }, [])


    // Animations-Texturen (Plane / Shield Init)
    useEffect(() => {
        planeTexture.colorSpace = SRGBColorSpace
        planeTexture.flipY = false
        planeTexture.needsUpdate = true

        shieldTexture.colorSpace = NoColorSpace
        shieldTexture.flipY = false
        shieldTexture.needsUpdate = true
    }, [planeTexture, shieldTexture])


    // Pointer-Events (Configurator-Drag)
    useEffect(() => {
        const passiveOptions = { passive: false } as const

        function handlePointerDown(event: PointerEvent) {
            if (store.appFlowPhase !== 'configurator') return
            if (!(event.target instanceof Element)) return
            if (
                event.target.closest('[data-disable-configurator-rotation]') ||
                !event.target.closest('[data-configurator-drag-zone]')
            ) {
                return
            }
            if (event.pointerType === 'touch') event.preventDefault()
            event.target.setPointerCapture(event.pointerId)
            configuratorIsDraggingRef.current = true
            configuratorLastClientXRef.current = event.clientX
        }

        function handlePointerMove(event: PointerEvent) {
            if (!configuratorIsDraggingRef.current) return
            if (event.pointerType === 'touch') event.preventDefault()
            const deltaX = event.clientX - configuratorLastClientXRef.current
            configuratorLastClientXRef.current = event.clientX
            configuratorRotationRef.current += deltaX * characterRotationDragSpeed
            applyCharacterRotation()
            window.dispatchEvent(new CustomEvent('configurator-rotation-change', {
                detail: { rotation: configuratorRotationRef.current }
            }))
        }

        function stopDragging(event: PointerEvent) {
            configuratorIsDraggingRef.current = false
            if (event.target instanceof Element) {
                try { event.target.releasePointerCapture(event.pointerId) } catch { /* ignore */ }
            }
        }

        window.addEventListener('pointerdown', handlePointerDown, passiveOptions)
        window.addEventListener('pointermove', handlePointerMove, passiveOptions)
        window.addEventListener('pointerup', stopDragging)
        window.addEventListener('pointercancel', stopDragging)

        return () => {
            window.removeEventListener('pointerdown', handlePointerDown)
            window.removeEventListener('pointermove', handlePointerMove)
            window.removeEventListener('pointerup', stopDragging)
            window.removeEventListener('pointercancel', stopDragging)
        }
    }, [])


    // Cleanup
    useEffect(() => {
        return () => {
            configuratorResetAnimationRef.current?.stop()
            phaseRotationAnimationRef.current?.stop()
            previewEffectRotationAnimationRef.current?.stop()
        }
    }, [])


    // Frame Loop
    useFrame((_, delta) => {
        skinMaterialsRef.current = getMeshBasicMaterials(skinMeshRef.current)

        // Hit-Reaktion Flash (einmalig beim ÃƒÅ“bergang)
        const isHitReactionActive = snapshot.hitReactionTimer > 0
        if (isHitReactionActive && !previousHitReactionActiveRef.current) {
            const outlineColor = characterOutlineCollectHeartColor
            outlineHandleRef.current?.setThickness(outlineCollectThickness)
            outlineHandleRef.current?.setStrength(outlineCollectStrength)
            outlineHandleRef.current?.setGlow(outlineCollectGlow)
            outlineHandleRef.current?.setPulsePeriod(outlineCollectPulsePeriod)
            outlineHandleRef.current?.setDownSampleRatio(outlineCollectSoftness)
            outlineHandleRef.current?.setAdditive(outlineCollectAdditive)
            outlineHandleRef.current?.setColor(withHalfSaturation(outlineColor))
            triggerSkinFlash(skinFlashRef, outlineColor, FLASH_DURATION)
            outlineFlashRef.current = { color: outlineColor, remaining: FLASH_DURATION }
        }
        previousHitReactionActiveRef.current = isHitReactionActive

        // Outline-Flash ausklingen
        if (outlineFlashRef.current) {
            outlineFlashRef.current.remaining -= delta
            if (outlineFlashRef.current.remaining <= 0) {
                outlineFlashRef.current = null
                applyOutlineState({
                    outline: outlineHandleRef.current,
                    defaultThickness: outlineDefaultThickness,
                    defaultColor: characterOutlineColor,
                    defaultStrength: outlineDefaultStrength,
                    defaultGlow: outlineDefaultGlow,
                    defaultPulsePeriod: outlineDefaultPulsePeriod,
                    defaultSoftness: outlineDefaultSoftness,
                    defaultAdditive: outlineDefaultAdditive,
                    collectThickness: outlineCollectThickness,
                    collectShieldColor: characterOutlineCollectShieldColor,
                    collectStrength: outlineCollectStrength,
                    collectGlow: outlineCollectGlow,
                    collectPulsePeriod: outlineCollectPulsePeriod,
                    collectSoftness: outlineCollectSoftness,
                    collectAdditive: outlineCollectAdditive,
                    isPlaneActive: isPlaneVisualActive,
                    isShieldActive: isShieldVisualActive,
                })
                applySkinColorState(
                    skinMaterialsRef.current,
                    store.chameleonColor,
                    characterOutlineCollectShieldColor,
                    isShieldVisualActive,
                )
            }
        }

        // Skin-Farbe animieren
        let targetSkinColor: Color | null = null

        if (skinFlashRef.current) {
            skinFlashRef.current.remaining -= delta
            targetSkinColor = skinFlashRef.current.color
            if (skinFlashRef.current.remaining <= 0) {
                skinFlashRef.current = null
                targetSkinColor = null
            }
        }

        if (!targetSkinColor && isPlaneVisualActive) {
            planeHueRef.current = (planeHueRef.current + delta * 0.8) % 1
            _outlineColor.current.setHSL(planeHueRef.current, 0.7, 0.5)
            outlineHandleRef.current?.setColor(_outlineColor.current)
            targetSkinColor = _outlineColor.current
        }

        if (!targetSkinColor) {
            targetSkinColor = withHalfSaturation(
                isShieldVisualActive ? characterOutlineCollectShieldColor : store.chameleonColor,
            )
        }

        applySkinFlashColor(skinMaterialsRef.current, targetSkinColor, delta)

        // Lane-Turn Rotation
        if (laneTurnRotationGroupRef.current) {
            laneTurnRotationGroupRef.current.rotation.y =
                store.appFlowPhase === 'run' && laneTurnAnimationRef.current !== undefined
                    ? laneTurnAnimationRef.current
                    : 0
        }

        applyCharacterRotation()
    })


    // JSX
    const position = snapshot.characterVisualPosition
    const showConfiguratorEffectPreview =
        store.appFlowPhase === 'configurator' &&
        store.configuratorActiveTab === 'effect'
    const selectedEffectAnchorRef = effectGroupRef
    const selectedEffectTimelineRef = showConfiguratorEffectPreview
        ? previewTimelineRef
        : footstepTimelineRef
    const selectedRunEffect = (() => {
        switch (store.footstepEffect) {
            case '': return null
            case 'leaves': return <EffectRunLeaves anchorRef={selectedEffectAnchorRef} timelineRef={selectedEffectTimelineRef} />
            case 'glow': return <EffectRunGlow anchorRef={selectedEffectAnchorRef} timelineRef={selectedEffectTimelineRef} />
            default: return <EffectRunDust anchorRef={selectedEffectAnchorRef} timelineRef={selectedEffectTimelineRef} />
        }
    })()

    return (
        <>
            <CharacterShadow />
            <CharacterOutlineEffect controller={outlineController} target={scene} responsiveThickness={!isStele} />
            <group
                scale={0.3}
                position={[
                    position[0] + characterPositionOffset[0],
                    position[1] + characterPositionOffset[1],
                    position[2] + characterPositionOffset[2],
                ]}
            >
                <group ref={previewEffectRotationGroupRef}>
                    <group
                        ref={previewEffectAnchorRef}
                        position={[0, effectPreviewHeight, effectPreviewDistance]}
                    />
                </group>
                <group ref={configuratorRotationGroupRef}>
                    <group ref={laneTurnRotationGroupRef}>
                        <group ref={effectGroupRef}>
                            <LevelCompleteCard>
                                <primitive object={scene} />
                            </LevelCompleteCard>
                            <EffectRunFootsteps anchorRef={effectGroupRef} timelineRef={footstepTimelineRef} />
                            <EffectJump anchorRef={effectGroupRef} timelineRef={footstepTimelineRef} />
                            <ShieldBreakEffect />
                            {selectedRunEffect}
                        </group>
                    </group>
                </group>
            </group>
        </>
    )
})
